import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil, tap, map } from 'rxjs/operators';
import { Tank } from '../shared/models/Tank';
import { TANKS_URL } from '../shared/constats/urls';
import { ToastrService } from 'ngx-toastr';
import { ChargeService } from './charge.service';
import { DischargeService } from './discharge.service';
import { Charge } from '../shared/models/Charge';
import { Discharge } from '../shared/models/Discharge';
import { User } from '../shared/models/User';

const TANK_KEY = 'Tank';

@Injectable({
  providedIn: 'root',
})
export class TankService {
  private tankSubject = new BehaviorSubject<Tank>(this.getTankFromLocalStorage());
  public tankObservable: Observable<Tank>;
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private toastrService: ToastrService,
    private chargeService: ChargeService,
    private dischargeService: DischargeService
  ) {
    this.tankObservable = this.tankSubject.asObservable().pipe(takeUntil(this.destroy$));
  }

  private getTankFromLocalStorage(): Tank | undefined {
    const tankData = localStorage.getItem('Tank');

    if (tankData) {
      try {
        const tank = JSON.parse(tankData);
        return tank;
      } catch (error) {
        console.error('Error al analizar los datos del tanque desde el almacenamiento local', error);
      }
    }

    return undefined;
  }

  private setTankToLocalStorage(tank: Tank) {
    localStorage.setItem(TANK_KEY, JSON.stringify(tank));
  }

  private tankSubjectList = new Subject<Tank[]>();
  tankObservableList: Observable<Tank[]> = this.tankSubjectList.asObservable().pipe(takeUntil(this.destroy$));

  getAll(): Observable<Tank[]> {
    return this.http.get<Tank[]>(TANKS_URL).pipe(
      tap((tankList) => {
        this.tankSubjectList.next(tankList);
      }),
      takeUntil(this.destroy$)
    );
  }

  getTankByUser(userId: number, tankId: number): Observable<Tank[]> {
    //return this.http.get<Tank[]>(TANKS_URL + '/getTankByUser/' + userId).pipe
    return this.http.get<Tank[]>(`${TANKS_URL}/getTankByUser/${tankId}/${userId}`).pipe
      (
        tap((tankList) => {
          this.tankSubjectList.next(tankList);
        }),
        takeUntil(this.destroy$)
      );
  }

  saveTank(tank: Tank): Observable<any> {
    tank.available = 0;
    return this.http.post(TANKS_URL + '/create', tank).pipe(
      catchError((error) => {
        this.toastrService.error('Error al guardar el tanque', 'Error');
        throw error;
      }),
      switchMap(() => this.getAll()),
      tap((tankList) => this.tankSubjectList.next(tankList)),
      tap(() => this.toastrService.success('Tanque guardado con éxito', 'Éxito')),
      takeUntil(this.destroy$)
    );
  }

  editTank(id: number, tank: Tank): Observable<any> {
    return this.http.put(TANKS_URL + '/' + id, tank).pipe(takeUntil(this.destroy$));
  }

  updateEncoderSpeed(id: number, encoderSpeed: number, password: string): Observable<Tank> {
    return this.http.put<Tank>(TANKS_URL + '/encoderSpeed/' + id, {
      encoderSpeed,
      password,
    });
  }

  getTank(id: number): Observable<Tank> {
    return this.http.get<Tank>(TANKS_URL + '/' + id).pipe(takeUntil(this.destroy$));
  }

  deleteTank(id: number): Observable<any> {
    return this.http.delete(TANKS_URL + '/' + id).pipe(takeUntil(this.destroy$));
  }

  setCurrentTank(tank: Tank) {
    this.setTankToLocalStorage(tank);
    this.tankSubject.next(tank);
  }

  getCurrentTank(): Observable<Tank> {
    return this.tankObservable;
  }

  public get currentTank(): Tank {
    return this.tankSubject.value;
  }

  isWebAccessEnabled(tank?: Tank): boolean {
    return !tank || tank.webAccessEnabled !== false;
  }

  public updateTankAvailable(tankId: number) {
    this.getTank(tankId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tankToUpdate: Tank) => {
        this.chargeService
          .getChargeByTank(tankId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((charges: Charge[]) => {
            let totalChargeAmount = charges.reduce((sum, charge) => sum + charge.amount, 0);

            this.dischargeService
              .getDischargeByTank(tankId)
              .pipe(takeUntil(this.destroy$))
              .subscribe((discharges: Discharge[]) => {
                let totalDischargeAmount = discharges.reduce((sum, discharge) => sum + discharge.amount, 0);
                let newAvailable = totalChargeAmount - totalDischargeAmount;

                tankToUpdate.available = newAvailable;

                this.editTank(tankId, tankToUpdate)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe(
                    () => {
                      this.setTankToLocalStorage(tankToUpdate);
                      console.log('Tanque actualizado correctamente');
                    },
                    (error) => {
                      console.error('Error al actualizar el tanque:', error);
                    }
                  );
              });
          });
      });
  }

  getLowFilterWarningsByUser(user: User): Observable<string | null> {
    if (user.role !== 'admin') {
      return new Observable<string | null>((observer) => {
        observer.next(null);
        observer.complete();
      });
    }

    return this.getTank(user.tankId).pipe(
      map((tank: Tank) => {
        const filterPercent = ((tank.filterLife - tank.amountFilter) * 100) / tank.filterLife;
        return filterPercent < 3
          ? `El filtro del tanque ${tank.name} está por debajo del 3%. Comunicate con Rimagro para su reemplazo.`
          : null;
      })
    );
  }

  getLowFuelWarningByUser(user: User): Observable<string | null> {
    if (user.role !== 'admin') {
      return new Observable<string | null>((observer) => {
        observer.next(null);
        observer.complete();
      });
    }

    return this.getTank(user.tankId).pipe(
      map((tank: Tank) => {
        if (tank.available < tank.availableAlert) {
          return `El tanque "${tank.name}" está por debajo del nivel mínimo. Por favor, agregá stock.`;
        }
        return null;
      })
    );
  }

  destroySubscriptions(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}



